import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
type LegalTab = 'terms' | 'privacy';
interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: LegalTab;
}
export function LegalModal({
  isOpen,
  onClose,
  initialTab = 'terms'
}: LegalModalProps) {
  const [activeTab, setActiveTab] = useState<LegalTab>(initialTab);
  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, initialTab]);
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose} data-id="element-89">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" data-id="element-90" />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-91">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0" data-id="element-92">
          <div className="flex gap-1" data-id="element-93">
            <button onClick={() => setActiveTab('terms')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${activeTab === 'terms' ? 'bg-navy text-white' : 'text-text-muted hover:bg-gray-100'}`} data-id="element-94">
              Términos y Condiciones
            </button>
            <button onClick={() => setActiveTab('privacy')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${activeTab === 'privacy' ? 'bg-navy text-white' : 'text-text-muted hover:bg-gray-100'}`} data-id="element-95">
              Política de Privacidad
            </button>
          </div>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100 transition-colors text-gray-500" aria-label="Cerrar" data-id="element-96">
            <X className="w-5 h-5" data-id="element-97" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto px-5 py-5 text-sm text-text-dark leading-relaxed space-y-4" data-id="element-98">
          {activeTab === 'terms' ? <>
              <h3 className="font-serif text-xl text-navy font-bold" data-id="element-99">
                Términos y Condiciones
              </h3>
              <p data-id="element-100">
                <strong data-id="element-101">Última actualización:</strong> Febrero 2026
              </p>

              <h4 className="font-semibold text-navy mt-4" data-id="element-102">
                1. Producto Digital
              </h4>
              <p data-id="element-103">
                "Silencia a la Mamá Juez" es un producto digital descargable que
                incluye un ebook en PDF, plantillas imprimibles y material
                complementario. Al completar la compra, recibirás acceso
                inmediato al contenido mediante descarga directa.
              </p>

              <h4 className="font-semibold text-navy mt-4" data-id="element-104">2. Uso Personal</h4>
              <p data-id="element-105">
                El contenido adquirido es para uso personal y no comercial.
                Queda prohibida la reproducción, distribución, modificación o
                reventa del material sin autorización expresa por escrito.
              </p>

              <h4 className="font-semibold text-navy mt-4" data-id="element-106">
                3. Política de Reembolso
              </h4>
              <p data-id="element-107">
                Dado que se trata de un producto digital con entrega inmediata,
                no se ofrecen reembolsos una vez completada la descarga. Si
                experimentas problemas técnicos con la descarga, contáctanos y
                te ayudaremos a resolverlo.
              </p>

              <h4 className="font-semibold text-navy mt-4" data-id="element-108">
                4. Descargo de Responsabilidad
              </h4>
              <p data-id="element-109">
                Este producto tiene fines educativos e informativos. No
                sustituye el consejo médico, psicológico o terapéutico
                profesional. Si sientes que necesitas apoyo clínico, te
                recomendamos consultar con un profesional de salud mental.
              </p>

              <h4 className="font-semibold text-navy mt-4" data-id="element-110">
                5. Propiedad Intelectual
              </h4>
              <p data-id="element-111">
                Todo el contenido, incluyendo textos, diseños, plantillas y
                metodología (Protocolo R.E.S.P.O.N.D.E.), está protegido por
                derechos de autor. Todos los derechos reservados © 2026.
              </p>

              <h4 className="font-semibold text-navy mt-4" data-id="element-112">
                6. Modificaciones
              </h4>
              <p data-id="element-113">
                Nos reservamos el derecho de actualizar estos términos. Los
                cambios serán efectivos desde su publicación en esta página.
              </p>
            </> : <>
              <h3 className="font-serif text-xl text-navy font-bold" data-id="element-114">
                Política de Privacidad
              </h3>
              <p data-id="element-115">
                <strong data-id="element-116">Última actualización:</strong> Febrero 2026
              </p>

              <h4 className="font-semibold text-navy mt-4" data-id="element-117">
                1. Información que Recopilamos
              </h4>
              <p data-id="element-118">
                Al realizar tu compra, recopilamos únicamente la información
                necesaria para procesar la transacción: nombre, correo
                electrónico y datos de pago. Los datos de pago son procesados de
                forma segura por nuestro proveedor de pagos y no almacenamos
                información de tarjetas de crédito.
              </p>

              <h4 className="font-semibold text-navy mt-4" data-id="element-119">
                2. Uso de la Información
              </h4>
              <p data-id="element-120">Utilizamos tu información para:</p>
              <ul className="list-disc pl-5 space-y-1" data-id="element-121">
                <li data-id="element-122">Procesar y entregar tu compra</li>
                <li data-id="element-123">Enviarte el enlace de descarga</li>
                <li data-id="element-124">Comunicarnos contigo sobre tu pedido</li>
                <li data-id="element-125">
                  Enviarte contenido relacionado (solo si das tu consentimiento)
                </li>
              </ul>

              <h4 className="font-semibold text-navy mt-4" data-id="element-126">
                3. Protección de Datos
              </h4>
              <p data-id="element-127">
                Implementamos medidas de seguridad para proteger tu información
                personal. No vendemos, intercambiamos ni transferimos tu
                información a terceros, excepto a proveedores de servicios
                necesarios para completar la transacción.
              </p>

              <h4 className="font-semibold text-navy mt-4" data-id="element-128">4. Cookies</h4>
              <p data-id="element-129">
                Utilizamos cookies esenciales para el funcionamiento del sitio y
                cookies analíticas para mejorar la experiencia. Puedes
                desactivar las cookies en la configuración de tu navegador.
              </p>

              <h4 className="font-semibold text-navy mt-4" data-id="element-130">5. Tus Derechos</h4>
              <p data-id="element-131">Tienes derecho a:</p>
              <ul className="list-disc pl-5 space-y-1" data-id="element-132">
                <li data-id="element-133">Acceder a tus datos personales</li>
                <li data-id="element-134">Solicitar la corrección de datos inexactos</li>
                <li data-id="element-135">Solicitar la eliminación de tus datos</li>
                <li data-id="element-136">Retirar tu consentimiento para comunicaciones</li>
              </ul>

              <h4 className="font-semibold text-navy mt-4" data-id="element-137">6. Contacto</h4>
              <p data-id="element-138">
                Para cualquier consulta sobre privacidad o para ejercer tus
                derechos, puedes contactarnos por correo electrónico.
              </p>
            </>}
        </div>
      </div>
    </div>;
}